import { useEffect, useState } from "react";
import { CButton, CButtonGroup, CFormGroup } from "@coreui/react";
import { getItem, updateItem } from "../../api/generics";
import TaxPayer, { TaxPayerSpecialPermissions } from "../../models/tax-payer";
import { SUCCESS } from "../../utils/constants/tags";
import Loading from "../indicators/Loading";
import { warningAlert } from "../utils/messages";

interface Props {
  taxPayer: TaxPayer;
  onCancel: () => void;
  onSuccess: (permissions: TaxPayerSpecialPermissions) => void;
  onBusyChange?: (busy: boolean) => void;
}

const TaxPayerSpecialPermissionsForm = ({
  taxPayer,
  onCancel,
  onSuccess,
  onBusyChange,
}: Props) => {
  const [permissions, setPermissions] = useState<TaxPayerSpecialPermissions>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [retry, setRetry] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const url = `/tax_payers/${taxPayer.id}/special_permissions/`;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setPermissions(undefined);
    setLoadError("");
    const load = async () => {
      try {
        const result = await getItem<TaxPayerSpecialPermissions>(url);
        if (!active) return;
        if (
          result.status === SUCCESS &&
          typeof result.data?.whatsappEnabled === "boolean"
        ) {
          setPermissions(result.data);
        } else {
          setLoadError(
            result.detail || "No se pudieron cargar los permisos especiales.",
          );
        }
      } catch {
        if (active)
          setLoadError("No se pudieron cargar los permisos especiales.");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [url, retry]);

  const save = async () => {
    if (!permissions || submitting) return;
    setSubmitting(true);
    onBusyChange?.(true);
    let saved: TaxPayerSpecialPermissions | undefined;
    try {
      const result = await updateItem<TaxPayerSpecialPermissions>(
        url,
        permissions,
      );
      if (
        result.status === SUCCESS &&
        typeof result.data?.whatsappEnabled === "boolean"
      ) {
        saved = result.data;
      } else {
        warningAlert(
          result.detail || "No se pudieron guardar los permisos especiales.",
        );
      }
    } catch {
      warningAlert("No se pudieron guardar los permisos especiales.");
    } finally {
      setSubmitting(false);
      onBusyChange?.(false);
    }
    if (saved) onSuccess(saved);
  };

  if (loading) return <Loading height="20vh" />;

  return (
    <>
      {loadError ? (
        <div className="alert alert-danger" role="alert">
          {loadError}
        </div>
      ) : (
        permissions && (
          <CFormGroup>
            <div className="custom-control custom-switch">
              <input
                id="special-permissions-whatsapp"
                type="checkbox"
                role="switch"
                className="custom-control-input"
                checked={permissions.whatsappEnabled}
                disabled={submitting}
                aria-describedby="special-permissions-whatsapp-description"
                onChange={(event) =>
                  setPermissions({
                    whatsappEnabled: event.currentTarget.checked,
                  })
                }
              />
              <label
                className="custom-control-label"
                htmlFor="special-permissions-whatsapp"
              >
                Soporte de WhatsApp
              </label>
            </div>
            <small
              id="special-permissions-whatsapp-description"
              className="form-text text-muted"
            >
              Permite configurar WhatsApp en los timbrados de este
              contribuyente. Al deshabilitarlo se detienen los envíos y se
              conserva la configuración.
            </small>
          </CFormGroup>
        )
      )}
      <div className="d-flex justify-content-end">
        <CButtonGroup>
          <CButton color="secondary" onClick={onCancel} disabled={submitting}>
            Salir
          </CButton>
          {loadError ? (
            <CButton
              color="primary"
              onClick={() => setRetry((value) => value + 1)}
            >
              Reintentar
            </CButton>
          ) : (
            <CButton
              color="primary"
              onClick={save}
              disabled={submitting || !permissions}
            >
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          )}
        </CButtonGroup>
      </div>
    </>
  );
};

export default TaxPayerSpecialPermissionsForm;
